import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Text,
  VStack,
  IconButton
} from '@chakra-ui/react';
import { EditIcon } from '@chakra-ui/icons';
import { KMDeckButton, KMDeckPage, KMDeckTheme } from '../../types/KMDeck';
import { isMobileDevice } from '../../utils/deviceDetection';
import { 
  formatR1C1, 
  getAllR1C1Positions,
  getEffectiveDimensions
} from '../../utils/r1c1Utils';

interface ButtonGridProps {
  page: KMDeckPage;
  rows: number;
  columns: number;
  onButtonClick: (button: KMDeckButton) => void;
  onButtonEdit: (button: KMDeckButton | null, r1C1Position: string) => void;
  onButtonSwap?: (fromR1C1: string, toR1C1: string) => void;
  onButtonCopy?: (fromR1C1: string, toR1C1: string) => void;
  availableHeight?: number; // Optional available height for desktop layout
  isLandscape?: boolean; // Whether the grid should be displayed in landscape orientation
  hideButtonLabels?: boolean; // Whether to hide R1C1 position labels on buttons
  isSplitPortraitLandscape?: boolean; // Whether portrait and landscape have separate button configurations
  glowingButtons?: Set<string>; // Set of button IDs that should glow
  theme?: KMDeckTheme; // Color theme for the grid
}

const ButtonGrid: React.FC<ButtonGridProps> = ({
  page,
  rows,
  columns,
  onButtonClick,
  onButtonEdit,
  onButtonSwap,
  onButtonCopy,
  availableHeight,
  isLandscape: propIsLandscape = false,
  hideButtonLabels = false,
  glowingButtons = new Set(),
  theme
}) => {
  const isMobile = isMobileDevice();
  const [isLandscape, setIsLandscape] = useState(propIsLandscape);
  const [gridDimensions, setGridDimensions] = useState({ buttonSize: 60, gridWidth: 300, gridHeight: 300, gap: 8 });
  
  // Drag and drop state
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartPosition, setDragStartPosition] = useState<string>('');
  const [dragOverPosition, setDragOverPosition] = useState<string>('');
  const [mouseDownPosition, setMouseDownPosition] = useState<{ x: number; y: number } | null>(null);
  const [hasMoved, setHasMoved] = useState(false);
  const [isAltKeyHeld, setIsAltKeyHeld] = useState(false);
  const clickHandledRef = useRef(false);

  // Debug component state (uncomment if needed)
  // console.log('[ButtonGrid] Component state:', {
  //   isMobile,
  //   isLandscape,
  //   rows,
  //   columns,
  //   effectiveRows: isLandscape ? columns : rows,
  //   effectiveColumns: isLandscape ? rows : columns
  // });

  // Calculate effective grid dimensions based on orientation
  const effectiveDimensions = getEffectiveDimensions(rows, columns, isLandscape);
  const effectiveRows = effectiveDimensions.rows;
  const effectiveColumns = effectiveDimensions.columns;

  // Calculate grid dimensions function with explicit orientation parameter
  const calculateGridDimensionsWithOrientation = useCallback((landscapeMode: boolean) => {
    // Calculate effective grid dimensions based on the provided orientation
    const effectiveDimensions = getEffectiveDimensions(rows, columns, landscapeMode);
    const effectiveRows = effectiveDimensions.rows;
    const effectiveColumns = effectiveDimensions.columns;
    
    // Use actual viewport dimensions for maximum space utilization
    const viewportWidth = window.innerWidth;
    const viewportHeight = availableHeight || window.innerHeight;
    
    // Determine if we're on mobile based on viewport size, not breakpoint
    const isMobileDevice = viewportWidth < 768 || (viewportWidth < 1024 && viewportHeight < 600);
    
    // Define padding based on device type and orientation
    let padding: number;
    if (isMobileDevice) {
      // In landscape mode, use minimal padding to maximize button size
      padding = landscapeMode ? 8 : 16;
    } else {
      padding = 32;
    }
    
    const gap = isMobileDevice ? 8 : 12;
    
    // Calculate actual available space for the grid
    const availableWidth = viewportWidth - (padding * 2);
    const availableHeightForGrid = viewportHeight - (padding * 2);
    
    // Calculate space needed for gaps between buttons using effective dimensions
    const totalGapWidth = gap * (effectiveColumns - 1);
    const totalGapHeight = gap * (effectiveRows - 1);
    
    // Calculate space available for actual buttons
    const buttonSpaceWidth = availableWidth - totalGapWidth;
    const buttonSpaceHeight = availableHeightForGrid - totalGapHeight;
    
    // Calculate maximum button size that fits
    const maxWidthPerButton = buttonSpaceWidth / effectiveColumns;
    const maxHeightPerButton = buttonSpaceHeight / effectiveRows;
    
    // Use the smaller dimension to ensure square buttons that fit
    const calculatedButtonSize = Math.min(maxWidthPerButton, maxHeightPerButton);
    
    // Apply reasonable size constraints - more flexible for better screen utilization
    let minButtonSize: number;
    let maxButtonSize: number;
    
    if (isMobileDevice) {
      if (landscapeMode) {
        // In landscape mode, be more aggressive with sizing
        minButtonSize = 25;
        maxButtonSize = 140;
      } else {
        // Portrait mode - standard constraints
        minButtonSize = 30;
        maxButtonSize = 120;
      }
    } else {
      minButtonSize = 40;
      maxButtonSize = 150;
    }
    
    const finalButtonSize = Math.max(minButtonSize, Math.min(maxButtonSize, calculatedButtonSize));
    
    // Calculate actual grid dimensions using effective dimensions
    const actualGridWidth = (finalButtonSize * effectiveColumns) + totalGapWidth;
    const actualGridHeight = (finalButtonSize * effectiveRows) + totalGapHeight;
    
    const result = {
      buttonSize: finalButtonSize,
      gridWidth: actualGridWidth,
      gridHeight: actualGridHeight,
      gap: gap
    };
    
    // Debug grid dimensions (uncomment if needed)
    // console.log('[ButtonGrid] Grid dimensions calculated with orientation:', {
    //   landscapeMode,
    //   isMobileDevice,
    //   effectiveRows,
    //   effectiveColumns,
    //   viewportWidth,
    //   viewportHeight,
    //   availableWidth,
    //   availableHeightForGrid,
    //   totalGapWidth,
    //   totalGapHeight,
    //   calculatedButtonSize,
    //   finalButtonSize,
    //   actualGridWidth,
    //   actualGridHeight,
    //   result
    // });
    
    return result;
  }, [rows, columns, availableHeight]);


  // Handle orientation changes and recalculate dimensions
  useEffect(() => {
    let resizeTimeout: NodeJS.Timeout;
    let lastWidth = window.innerWidth;
    let lastHeight = window.innerHeight;
    
    const checkOrientation = () => {
      // On desktop, use the prop value directly for manual orientation switching
      if (!isMobile) {
        setIsLandscape(propIsLandscape);
        const newDimensions = calculateGridDimensionsWithOrientation(propIsLandscape);
        setGridDimensions(newDimensions);
        return;
      }
      
      // On mobile, use the prop value directly since the parent component
      // (KMDeck) now handles mobile orientation detection
      const shouldRotate = propIsLandscape;
      
      // Debug orientation check
      console.log('[ButtonGrid] Orientation check:', {
        propIsLandscape,
        shouldRotate,
        originalRows: rows,
        originalColumns: columns,
        isMobile
      });
      
      // Update landscape state based on whether we should rotate
      setIsLandscape(shouldRotate);
      
      // Calculate grid dimensions with the rotation state
      const newDimensions = calculateGridDimensionsWithOrientation(shouldRotate);
      setGridDimensions(newDimensions);
    };

    const handleResize = () => {
      const currentWidth = window.innerWidth;
      const currentHeight = window.innerHeight;
      
      // Only proceed if dimensions actually changed
      if (currentWidth !== lastWidth || currentHeight !== lastHeight) {
        lastWidth = currentWidth;
        lastHeight = currentHeight;
        
        // Clear any existing timeout
        clearTimeout(resizeTimeout);
        
        // Wait for window to be stable for 300ms before resizing
        resizeTimeout = setTimeout(() => {
          checkOrientation();
        }, 300);
      }
    };

    // Check initial orientation and calculate dimensions
    checkOrientation();

    // Listen for orientation changes (immediate for mobile)
    const handleOrientationChange = () => {
      // Only handle orientation changes on mobile devices
      if (!isMobile) return;
      
      // Debug orientation change (uncomment if needed)
      // console.log('[ButtonGrid] Orientation change detected');
      // Immediate check for orientation change
      setTimeout(checkOrientation, 50);
      // Also check after a longer delay to catch any delayed resize events
      setTimeout(checkOrientation, 300);
      // Additional check after a longer delay to ensure everything is settled
      setTimeout(checkOrientation, 600);
    };
    
    window.addEventListener('orientationchange', handleOrientationChange);

    // Listen for resize with stable size detection
    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(resizeTimeout);
      window.removeEventListener('orientationchange', handleOrientationChange);
      window.removeEventListener('resize', handleResize);
    };
  }, [isMobile, propIsLandscape, rows, columns, availableHeight, calculateGridDimensionsWithOrientation]);

  const handleButtonClick = useCallback((button: KMDeckButton, r1c1Position: string) => {
    const effectiveButton = button;
    
    // Check if button is empty - for toggle buttons, check if either state has configuration
    const isEmpty = !effectiveButton || (
      !effectiveButton.title && 
      !effectiveButton.macroUuid && 
      !(effectiveButton.isToggle && effectiveButton.toggleState1 && effectiveButton.toggleState2 && 
        (effectiveButton.toggleState1.title || effectiveButton.toggleState1.macroUuid || 
         effectiveButton.toggleState2.title || effectiveButton.toggleState2.macroUuid))
    );
    
    if (isEmpty) {
      // Empty button - open editor to create new button
      onButtonEdit(button, r1c1Position);
    } else {
      // Configured button - trigger the macro
      onButtonClick(effectiveButton);
    }
  }, [onButtonClick, onButtonEdit]);

  // Helper function to get the button at a specific R1C1 position
  const getButtonAtR1C1 = useCallback((r1c1: string): KMDeckButton | null => {
    // Simple button lookup - the page passed to ButtonGrid is already the correct one
    return page.buttons.find(b => {
      // Extract R1C1 from button ID (format: button-pageId-R1C1)
      const parts = b.id.split('-');
      const buttonR1C1 = parts[parts.length - 1];
      return buttonR1C1 === r1c1;
    }) || null;
  }, [page.buttons]);

  // Document-level mouse event handlers for drag tracking
  useEffect(() => {
    const handleDocumentMouseMove = (e: MouseEvent) => {
      if (isMobile || !mouseDownPosition) return;
      
      // Check if Option/Alt key is held
      setIsAltKeyHeld(e.altKey);
      
      // Check if mouse has moved significantly (more than 5 pixels)
      const deltaX = Math.abs(e.clientX - mouseDownPosition.x);
      const deltaY = Math.abs(e.clientY - mouseDownPosition.y);
      
      if (deltaX > 5 || deltaY > 5) {
        setHasMoved(true);
        if (!isDragging) {
          setIsDragging(true);
        }
        
        // Find which button the mouse is over
        const element = document.elementFromPoint(e.clientX, e.clientY);
        if (element) {
          const buttonElement = element.closest('.kmdeck-button');
          if (buttonElement) {
            const r1c1Position = buttonElement.getAttribute('data-position') || '';
            if (r1c1Position && r1c1Position !== dragOverPosition) {
              setDragOverPosition(r1c1Position);
            }
          }
        }
      }
    };

  const handleDocumentMouseUp = () => {
    if (isMobile || !mouseDownPosition) return;
    
    if (hasMoved && isDragging && dragStartPosition) {
      // This was a drag operation
      if (dragOverPosition && dragStartPosition !== dragOverPosition) {
        // Find the button at the drag start position
        const fromButton = getButtonAtR1C1(dragStartPosition);
        
        // Find the button at the drop position
        const toButton = getButtonAtR1C1(dragOverPosition);
        
        console.log('[ButtonGrid] Drag operation:', {
          isLandscape,
          visualStart: dragStartPosition,
          visualOver: dragOverPosition,
          fromButton: fromButton?.id,
          toButton: toButton?.id,
          rows,
          columns,
          landscapePositions: (page as KMDeckPage & { landscapePositions?: unknown }).landscapePositions
        });
        
        if (isAltKeyHeld && onButtonCopy) {
          // Copy button settings to destination
          onButtonCopy(dragStartPosition, dragOverPosition);
        } else if (onButtonSwap) {
          // Swap buttons (default behavior)
          onButtonSwap(dragStartPosition, dragOverPosition);
        }
      }
    } else if (!hasMoved && dragStartPosition) {
      // This was a simple click, not a drag
      // Only handle on desktop when drag tracking is active
      if (!isMobile) {
        // Find the button at the click position
        const button = getButtonAtR1C1(dragStartPosition);
        
        if (button) {
          clickHandledRef.current = true;
          handleButtonClick(button, dragStartPosition);
        } else {
          // Empty slot clicked - open button editor
          clickHandledRef.current = true;
          onButtonEdit(null, dragStartPosition);
        }
      }
    }
    
    // Reset all drag state
    setIsDragging(false);
    setDragStartPosition('');
    setDragOverPosition('');
    setMouseDownPosition(null);
    setHasMoved(false);
    setIsAltKeyHeld(false);
    
    // Reset clickHandled after a short delay to allow onClick to check it first
    // This prevents the onClick from firing after we've already handled the click
    setTimeout(() => {
      clickHandledRef.current = false;
    }, 50);
  };

    if (mouseDownPosition) {
      document.addEventListener('mousemove', handleDocumentMouseMove);
      document.addEventListener('mouseup', handleDocumentMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleDocumentMouseMove);
      document.removeEventListener('mouseup', handleDocumentMouseUp);
    };
  }, [isMobile, mouseDownPosition, isDragging, dragStartPosition, dragOverPosition, hasMoved, page, isLandscape, rows, columns, effectiveRows, effectiveColumns, onButtonSwap, onButtonCopy, isAltKeyHeld, handleButtonClick, onButtonEdit, getButtonAtR1C1]);

  const buttonSize = `${gridDimensions.buttonSize}px`;
  const buttonGap = `${gridDimensions.gap}px`;

  const handleEditClick = (e: React.MouseEvent, button: KMDeckButton | null, r1c1Position: string) => {
    e.stopPropagation(); // Prevent triggering the button action
    onButtonEdit(button, r1c1Position);
  };

  const handleMouseDown = (e: React.MouseEvent, button: KMDeckButton | null, r1c1Position: string) => {
    if (isMobile) return;
    
    e.preventDefault();
    setMouseDownPosition({ x: e.clientX, y: e.clientY });
    setDragStartPosition(r1c1Position);
    setHasMoved(false);
    // Reset clickHandled at the start of each interaction to ensure clean state
    clickHandledRef.current = false;
  };





  const renderButton = (button: KMDeckButton | null, r1c1Position: string) => {
    const effectiveButton = button;
    
    // Check if button is empty - for toggle buttons, check if either state has configuration
    const isEmpty = !effectiveButton || (
      !effectiveButton.title && 
      !effectiveButton.macroUuid && 
      !(effectiveButton.isToggle && effectiveButton.toggleState1 && effectiveButton.toggleState2 && 
        (effectiveButton.toggleState1.title || effectiveButton.toggleState1.macroUuid || 
         effectiveButton.toggleState2.title || effectiveButton.toggleState2.macroUuid))
    );
    const isDraggedOver = dragOverPosition === r1c1Position;
    const isBeingDragged = dragStartPosition === r1c1Position && isDragging;
    const isGlowing = effectiveButton ? glowingButtons.has(effectiveButton.id) : false;
    const canDrag = !isMobile;
    const isCopyMode = isDragging && isAltKeyHeld;
    
    // Debug logging for glow state
    if (isGlowing) {
      console.log('[ButtonGrid] Button is glowing:', r1c1Position, 'buttonId:', effectiveButton?.id);
      console.log('[ButtonGrid] Glow boxShadow will be applied');
    }
    
    return (
      <Box
        key={r1c1Position}
        className="kmdeck-button"
        data-position={r1c1Position}
        w={buttonSize}
        h={buttonSize}
        bg={isEmpty ? (theme?.emptyButtonBackground || '#1a1a1a') : (effectiveButton?.backgroundColor || '#1a1a1a')}
        borderRadius="lg"
        border="2px solid"
        borderColor={
          isDraggedOver 
            ? (isCopyMode ? '#38a169' : '#3182ce')
            : isBeingDragged 
            ? (isCopyMode ? '#38a169' : '#3182ce')
            : isEmpty 
            ? (theme?.buttonEdges || '#333333')
            : (theme?.buttonEdges || '#555555')
        }
        display="flex"
        alignItems="center"
        justifyContent="center"
        position="relative"
        overflow="hidden"
        cursor={canDrag ? (isDragging ? 'grabbing' : 'grab') : 'pointer'}
        transition="all 0.2s ease"
        opacity={isBeingDragged ? 0.5 : 1}
        transform={isDraggedOver ? 'scale(1.05)' : 'scale(1)'}
        boxShadow={(() => {
          const glowColor = theme?.glowEffect || '#ffa500';
          const glowDisabled = theme?.glowDisabled || false;
          const shadow = isGlowing && !glowDisabled
            ? `0 0 15px ${glowColor}90, 0 0 30px ${glowColor}60, 0 0 45px ${glowColor}30`
            : isDraggedOver 
            ? (isCopyMode 
                ? '0 0 0 2px #38a169, 0 4px 12px rgba(56, 161, 105, 0.3)'
                : '0 0 0 2px #3182ce, 0 4px 12px rgba(49, 130, 206, 0.3)')
            : isBeingDragged
            ? '0 4px 12px rgba(0,0,0,0.3)'
            : 'none';
          
          return shadow;
        })()}
        _hover={{
          transform: canDrag ? 'scale(1.02)' : 'scale(1.02)',
          borderColor: isEmpty ? (theme?.buttonEdges || '#555555') : (theme?.buttonEdges || '#777777'),
          boxShadow: isGlowing && !(theme?.glowDisabled || false)
            ? `0 0 15px ${theme?.glowEffect || '#ffa500'}90, 0 0 30px ${theme?.glowEffect || '#ffa500'}60, 0 0 45px ${theme?.glowEffect || '#ffa500'}30`
            : (isEmpty ? 'none' : '0 4px 12px rgba(0,0,0,0.3)')
        }}
        _active={{
          transform: 'scale(0.98)'
        }}
        style={{
          backgroundImage: effectiveButton?.backgroundImageUrl 
            ? `url(${effectiveButton.backgroundImageUrl})` 
            : undefined,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          e.nativeEvent.stopImmediatePropagation();
          
          // Check if this click was already handled by document mouseup
          if (clickHandledRef.current) {
            return;
          }
          
          // For mobile or when drag tracking is not active, handle click immediately
          if (isMobile || !mouseDownPosition) {
            if (button) {
              handleButtonClick(button, r1c1Position);
            }
            return;
          }
          
          // For desktop with drag tracking, only handle if we haven't moved (no drag)
          if (!hasMoved && dragStartPosition === r1c1Position) {
            clickHandledRef.current = true;
            if (button) {
              handleButtonClick(button, r1c1Position);
            }
          }
        }}
        onMouseDown={(e) => handleMouseDown(e, button, r1c1Position)}
      >
        {/* Button Content */}
        <VStack spacing={0} align="center" justify="center" h="full" w="full" pointerEvents="none">
          {isEmpty ? (
            !isMobile && (
              <Text
                color="#666666"
                fontSize="xs"
                textAlign="center"
                fontWeight="medium"
                px={2}
                pointerEvents="none"
              >
                Click to add
              </Text>
            )
          ) : !effectiveButton.hideTitle ? (
            <Text
              color={effectiveButton.fontColor || 'white'}
              fontSize={`${effectiveButton.fontSize || 18}px`}
              fontFamily={effectiveButton.fontFamily || 'Arial, sans-serif'}
              fontWeight="medium"
              textAlign="center"
              textShadow="0 1px 2px rgba(0,0,0,0.8)"
              px={2}
              lineHeight="1.2"
              whiteSpace="pre-line"
              pointerEvents="none"
            >
              {effectiveButton.title}
            </Text>
          ) : null}
        </VStack>

        {/* Copy Mode Indicator */}
        {isCopyMode && isDraggedOver && (
          <Box
            position="absolute"
            top="2px"
            right="2px"
            bg="#38a169"
            color="white"
            borderRadius="full"
            w="20px"
            h="20px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            fontSize="12px"
            fontWeight="bold"
            boxShadow="0 2px 4px rgba(0,0,0,0.3)"
          >
            +
          </Box>
        )}

        {/* Toggle Button Indicator - Only show on desktop */}
        {!isEmpty && effectiveButton?.isToggle && !isMobile && (
          <Box
            position="absolute"
            top="2px"
            left="2px"
            bg="blue.500"
            color="white"
            borderRadius="full"
            w="16px"
            h="16px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            fontSize="10px"
            fontWeight="bold"
            boxShadow="0 2px 4px rgba(0,0,0,0.3)"
          >
            T
          </Box>
        )}


        {/* R1C1 Position Label - Only show on desktop and when not hidden */}
        {!isMobile && !hideButtonLabels && (
          <Box
            position="absolute"
            bottom="2px"
            left="2px"
            bg="rgba(0, 0, 0, 0.7)"
            color="white"
            borderRadius="sm"
            px={1}
            py={0.5}
            fontSize="10px"
            fontWeight="bold"
            textAlign="center"
            minW="24px"
            pointerEvents="none"
            zIndex={5}
          >
            {r1c1Position}
          </Box>
        )}

        {/* Edit Button - Only show on configured buttons and desktop */}
        {!isEmpty && !isMobile && (
          <IconButton
            position="absolute"
            top="2px"
            right="2px"
            aria-label="Edit button"
            icon={<EditIcon />}
            size="xs"
            colorScheme="blue"
            variant="solid"
            borderRadius="full"
            w="20px"
            h="20px"
            minW="20px"
            opacity={0.8}
            _hover={{ opacity: 1 }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleEditClick(e, button, r1c1Position);
            }}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            zIndex={10}
          />
        )}

      </Box>
    );
  };


  // Create button array - each button stays tied to its logical R1C1 position
  // The visual layout changes due to grid rotation, but buttons don't move
  const allR1C1Positions = getAllR1C1Positions(rows, columns);
  const buttonArray: { button: KMDeckButton | null; r1c1Position: string }[] = [];
  
  if (isLandscape) {
    // In landscape mode, reorder the buttons to match the rotated visual layout
    // Portrait: R1C1, R1C2, R1C3, R2C1, R2C2, R2C3, R3C1, R3C2, R3C3, R4C1, R4C2, R4C3, R5C1, R5C2, R5C3
    // Landscape: R1C3, R2C3, R3C3, R4C3, R5C3, R1C2, R2C2, R3C2, R4C2, R5C2, R1C1, R2C1, R3C1, R4C1, R5C1
    for (let col = columns; col >= 1; col--) {
      for (let row = 1; row <= rows; row++) {
        const r1c1Position = formatR1C1({ row, col });
        const button = getButtonAtR1C1(r1c1Position);
        buttonArray.push({ button, r1c1Position });
      }
    }
  } else {
    // In portrait mode, use standard row-major order
    for (const r1c1Position of allR1C1Positions) {
      const button = getButtonAtR1C1(r1c1Position);
      buttonArray.push({ button, r1c1Position });
    }
  }

  // Debug grid rendering (uncomment if needed)
  // console.log('[ButtonGrid] Rendering grid:', {
  //   isLandscape,
  //   effectiveRows,
  //   effectiveColumns,
  //   buttonSize,
  //   buttonGap,
  //   gridWidth: gridDimensions.gridWidth,
  //   gridHeight: gridDimensions.gridHeight,
  //   totalButtons: buttonArray.length,
  //   gridTemplateColumns: `repeat(${effectiveColumns}, ${buttonSize})`,
  //   gridTemplateRows: `repeat(${effectiveRows}, ${buttonSize})`
  // });

  return (
    <Box
      className="kmdeck-button-grid"
      display="grid"
      gridTemplateColumns={`repeat(${effectiveColumns}, ${buttonSize})`}
      gridTemplateRows={`repeat(${effectiveRows}, ${buttonSize})`}
      gap={buttonGap}
      w={`${gridDimensions.gridWidth}px`}
      h={`${gridDimensions.gridHeight}px`}
      mx="auto"
    >
      {buttonArray.map((item, _index) => renderButton(item.button, item.r1c1Position))}
    </Box>
  );
};

export default ButtonGrid;
